import { alpha, styled } from '@mui/material'
import useWindow from 'hooks/useWindow'
import { memo, useEffect } from 'react'
import { store } from 'store'
import { useCurrentSymbol } from 'store/market/hooks'
import { updateResolution } from 'store/orderbook/actions'
import { useCurrentChartTab, useResolution } from 'store/orderbook/hooks'
import { useCurrentThemeType } from 'store/settings/hooks'
import { Flex, MuiThemeType, SectionWrap } from 'styles'
import { darkColor } from '../../styles/constants/dark'
import { DataFeed } from '../../utils/datafeed'
import { DataFeed as BinanceDatafeed } from '../../utils/datafeed/binance'
import { ChartHeader } from './ChartHeader'
import Loading from 'components/Loading'
import { ReactComponent as LogoSvgIcon } from './assets/logo.svg'
import { TradingViewKLintType } from 'store/orderbook/reducer'
import clsx from 'clsx'
import { PerpInfo } from 'views/Perp/Info'
import TVHeader from './TVHeader'

const ChartWrap = styled(Flex)`
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 527px;
  background: ${({ theme }) => theme.color.bg};
  position: relative;

  ${(props) => props.theme.breakpoints.down('md')} {
    border: unset;
  }
`
const Wrap = styled(SectionWrap)`
  flex: 1;
  overflow: hidden;
`
const StyledChartContainer = styled('div')`
  display: none;
  flex-direction: column;
  min-height: 497px;
  height: 100%;
  position: relative;

  &.in {
    display: flex;
  }
`
const StyledChartMask = styled('div')`
  display: none;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.color.bg};
  color: ${({ theme }) => alpha(theme.color.primary10, 0.4)};

  &.in {
    display: flex;
  }
`

const StyledChartLogo = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  pointer-events: none;
`
function initOnReady(
  symbolName: string | undefined,
  theme: string,
  interval: string,
  currentChartTab: string,
  isMobile?: boolean,
) {
  const datafeed =
    currentChartTab === TradingViewKLintType.ZKEX
      ? new DataFeed()
      : new BinanceDatafeed()
  const upColor = 'rgba(14, 203, 129, 1)'
  const downColor = 'rgba(245, 69, 93, 1)'
  const $mask = document.getElementById('tv_chart_mask')
  if ($mask !== null) {
    $mask.classList.add('in')
  }

  const enabled_features: string[] = []
  // if (isMobile || currentChartTab === TradingViewKLintType.ORIGINAL) {
  //   enabled_features.push('hide_left_toolbar_by_default')
  // }

  const widget = (window.tvWidget = new window.TradingView.widget({
    symbol: symbolName,
    datafeed,
    interval: interval || '15',
    container: 'tv_chart_container',
    library_path: '/charting_library/',
    debug: false,
    autosize: true,
    // theme,
    theme: MuiThemeType.dark,
    custom_css_url: './css/chart_themed.css',
    loading_screen: {
      backgroundColor: MuiThemeType.dark,
      // theme === MuiThemeType.dark ? darkColor.bg : lightColor.bg,
    },
    overrides: {
      'paneProperties.backgroundType': 'solid',
      'paneProperties.background': darkColor.bg,
      // theme === MuiThemeType.dark ? darkColor.bg : lightColor.bg,
      'mainSeriesProperties.candleStyle.upColor': upColor,
      'mainSeriesProperties.candleStyle.borderUpColor': upColor,
      'mainSeriesProperties.candleStyle.wickUpColor': upColor,
      'mainSeriesProperties.candleStyle.downColor': downColor,
      'mainSeriesProperties.candleStyle.borderDownColor': downColor,
      'mainSeriesProperties.candleStyle.wickDownColor': downColor,
    },
    studies_overrides: {
      'volume.volume.color.0': 'rgba(245, 69, 93, 0.4)',
      'volume.volume.color.1': 'rgba(14, 203, 129, 0.4)',
    },
    enabled_features: enabled_features,
    disabled_features: [
      'left_toolbar',
      'border_around_the_chart',
      'header_symbol_search',
      'symbol_search_hot_key',
      'header_compare',
      'header_undo_redo',
      'go_to_date',
      'timeframes_toolbar',
      'header_saveload',
      'context_menus.legend_context_menu',
      'hide_last_na_study_output',
      'main_series_scale_menu',
      'save_chart_properties_to_local_storage',
      'hide_unresolved_symbols_in_legend',
      'hide_last_na_study_output',
      'chart_property_page_background',
      'custom_resolutions',
      'volume_force_overlay',
      'header_widget',
      // 'use_localstorage_for_settings',
      // 'header_symbol_search',
      // 'go_to_date',
      // 'header_compare',
      // 'volume_force_overlay',
      // 'popup_hints',
      // 'timeframes_toolbar',
      // 'header_undo_redo',
      // // 'header_resolutions',
      // // `${isMobile && 'timeframes_toolbar'}`,
      // `${isMobile && 'legend_widget'}`,
      // 'create_volume_indicator_by_default',
    ],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC',
  }))
  widget.onChartReady(() => {
    if ($mask !== null) {
      $mask.classList.remove('in')
    }
    widget
      .activeChart()
      .onIntervalChanged()
      .subscribe(null, (interval: string, timeframeObj: any) => {
        // console.log('interval----', interval)
        store.dispatch(updateResolution(interval))
      })
  })
}
export const Charting = memo(() => {
  const currentSymbol = useCurrentSymbol()
  const currentThemeType = useCurrentThemeType()
  const currentChartTab = useCurrentChartTab()
  const localInterval = useResolution()
  const { isMobile } = useWindow()

  useEffect(() => {
    if (!currentSymbol?.name) return
    if (
      currentChartTab === TradingViewKLintType.ZKEX ||
      currentChartTab === TradingViewKLintType.BINANCE
    )
      initOnReady(
        currentSymbol?.name,
        currentThemeType,
        localInterval,
        currentChartTab,
        isMobile,
      )
  }, [currentSymbol, currentChartTab, currentThemeType, isMobile])

  return (
    <ChartWrap id="zk_tv_chart">
      <ChartHeader />
      <TVHeader />
      <StyledChartContainer
        className={clsx({
          in:
            currentChartTab === TradingViewKLintType.ZKEX ||
            currentChartTab === TradingViewKLintType.BINANCE,
        })}
      >
        <Wrap id={'tv_chart_container'}></Wrap>
        <StyledChartMask id="tv_chart_mask">
          <Loading size={40} />
        </StyledChartMask>
        <StyledChartLogo>
          <LogoSvgIcon />
        </StyledChartLogo>
      </StyledChartContainer>
      <StyledChartContainer
        className={clsx({
          in: currentChartTab === TradingViewKLintType.INFO,
        })}
      >
        <PerpInfo symbolName={currentSymbol.name!} />
      </StyledChartContainer>
    </ChartWrap>
  )
})
